import React from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Stack,
  Chip,
  Paper,
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Cancel from '@mui/icons-material/Cancel'
import ApproveRejectDialog from './ApproveRejectDialog'

const DetailRow = ({ label, value, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
      {label}
    </Typography>
    {children || <Typography variant="body2">{value || '-'}</Typography>}
  </Box>
)

const StatusChip = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'PENDING':
        return { bg: '#fff3cd', color: '#856404', border: '#ffc107' }
      case 'APPROVED':
        return { bg: '#d4edda', color: '#155724', border: '#28a745' }
      case 'REJECTED':
      case 'DENIED':
        return { bg: '#f8d7da', color: '#721c24', border: '#dc3545' }
      case 'EXPIRED':
        return { bg: '#e2e3e5', color: '#383d41', border: '#6c757d' }
      case 'CANCELLED':
      case 'DELETED':
        return { bg: '#f5f5f5', color: '#6c757d', border: '#adb5bd' }
      default:
        return { bg: '#f5f5f5', color: '#6c757d', border: '#adb5bd' }
    }
  }
  const colors = getStatusColor()
  return (
    <Chip
      label={status}
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        fontWeight: 600,
        fontSize: '0.75rem',
      }}
      size="small"
    />
  )
}

export default function AuditRequestDetailsDrawer({ open, onClose, request, onApproveReject }) {
  const [approveRejectDialogOpen, setApproveRejectDialogOpen] = React.useState(false)

  if (!request) return null

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-'
    try {
      const date = new Date(dateTimeString)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      })
    } catch {
      return dateTimeString
    }
  }

  const isExpired = (expiryAt) => {
    if (!expiryAt) return false
    try {
      return new Date(expiryAt) < new Date()
    } catch {
      return false
    }
  }

  const handleApproveRejectClick = () => {
    setApproveRejectDialogOpen(true)
  }

  const handleApproveRejectSuccess = () => {
    setApproveRejectDialogOpen(false)
    onApproveReject?.(request)
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 500, md: 600 },
            maxWidth: '95vw',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              Audit Request Details
            </Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Request Information */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Request Information
            </Typography>
            <Stack spacing={2}>
              <DetailRow label="Request ID" value={request.id} />
              <DetailRow
                label="Status"
                value={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status={request.requestStatus} />
                    {request.approvalExpiry && isExpired(request.approvalExpiry) && (
                      <Chip label="Expired" size="small" color="error" />
                    )}
                  </Box>
                }
              />
              <DetailRow label="Created Time" value={formatDateTime(request.createdTime)} />
              <DetailRow label="Created By" value={request.createdBy || '-'} />
              <DetailRow label="Modified Time" value={formatDateTime(request.modifiedTime)} />
              <DetailRow label="Modified By" value={request.modifiedBy || '-'} />
            </Stack>
          </Paper>

          {/* Chamber Room Information */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Chamber Room Information
            </Typography>
            <Stack spacing={2}>
              <DetailRow label="Chamber Room ID" value={request.chamberRoomId} />
              <DetailRow label="Chamber Room Title" value={request.chamberRoomTitle} />
              <DetailRow label="Chamber Room Slug" value={request.chamberRoomSlug} />
            </Stack>
          </Paper>

          {/* Clinic Information */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Clinic Information
            </Typography>
            <Stack spacing={2}>
              <DetailRow label="Clinic Brand ID" value={request.clinicBrandId} />
              <DetailRow label="Clinic Brand Title" value={request.clinicBrandTitle} />
              <DetailRow label="Clinic Brand Slug" value={request.clinicBrandSlug} />
              <DetailRow label="Clinic Location ID" value={request.clinicLocationId} />
              <DetailRow label="Clinic Location Title" value={request.clinicLocationTitle} />
              <DetailRow label="Clinic Location Slug" value={request.clinicLocationSlug} />
            </Stack>
          </Paper>

          {/* Mentor Information */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Mentor Information
            </Typography>
            <Stack spacing={2}>
              <DetailRow label="Mentor to Audit ID" value={request.mentorToAuditId} />
              <DetailRow label="Auditor Mentor ID" value={request.auditorMentorId} />
            </Stack>
          </Paper>

          {/* Approval/Rejection Information */}
          {(request.requestStatus === 'APPROVED' || request.requestStatus === 'REJECTED') && (
            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                {request.requestStatus === 'APPROVED' ? 'Approval' : 'Rejection'} Information
              </Typography>
              <Stack spacing={2}>
                {request.approvedBy && (
                  <DetailRow label="Approved By" value={request.approvedBy} />
                )}
                {request.approvedAt && (
                  <DetailRow label="Approved At" value={formatDateTime(request.approvedAt)} />
                )}
                {request.rejectedBy && (
                  <DetailRow label="Rejected By" value={request.rejectedBy} />
                )}
                {request.rejectedAt && (
                  <DetailRow label="Rejected At" value={formatDateTime(request.rejectedAt)} />
                )}
                {request.approvalExpiry && (
                  <DetailRow
                    label="Approval Expiry"
                    value={
                      <Box>
                        <Typography variant="body2">{formatDateTime(request.approvalExpiry)}</Typography>
                        {isExpired(request.approvalExpiry) && (
                          <Chip label="Expired" size="small" color="error" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    }
                  />
                )}
                {request.approvingNote && (
                  <DetailRow label="Approval Note" value={request.approvingNote} />
                )}
                {request.rejectionNote && (
                  <DetailRow label="Rejection Note" value={request.rejectionNote} />
                )}
              </Stack>
            </Paper>
          )}

          {/* Actions */}
          {request.requestStatus === 'PENDING' && (
            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={handleApproveRejectClick}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle color="primary" />
                  <Typography variant="body2" fontWeight={600}>
                    Approve / Reject Request
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Drawer>

      <ApproveRejectDialog
        open={approveRejectDialogOpen}
        onClose={() => setApproveRejectDialogOpen(false)}
        request={request}
        onSuccess={handleApproveRejectSuccess}
      />
    </>
  )
}
